import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import MotiveRadar from "@/components/MotiveRadar";
import PlayerCard from "@/components/PlayerCard";
import { getMotiveIcon, scoreBand } from "@/lib/motives";
import { api, formatApiError } from "@/lib/api";
import { CheckCircle2, Trophy, Home } from "lucide-react";

/**
 * Result screen shown to the player after finishing the test.
 * Reads analysis from sessionStorage (set by PublicTest submit).
 */
export default function TestResult() {
  const { token } = useParams();
  const [motives, setMotives] = useState([]);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const raw = sessionStorage.getItem("fmp_test_result");
        if (raw) setResult(JSON.parse(raw));
      } catch {}
      try {
        const { data } = await api.get("/meta/motives");
        setMotives(data.motives);
      } catch (e) {
        setErr(formatApiError(e));
      }
    })();
  }, [token]);

  if (!result)
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="text-center">
          <div className="font-display text-xs tracking-[0.3em] text-[#CCFF00] mb-3">
            / FMP
          </div>
          <div className="text-white/60">
            Kein Ergebnis gefunden. Bitte den Test erneut starten oder den Trainer kontaktieren.
          </div>
        </div>
      </div>
    );

  const { analysis, player, team } = result;
  const { archetype, top_motives, scores } = analysis;

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <div className="border-b border-white/5 py-8">
        <div className="mx-auto max-w-6xl px-6 flex items-center gap-3">
          <CheckCircle2 className="w-6 h-6 text-[#CCFF00]" />
          <div>
            <div className="font-display text-xs tracking-[0.3em] text-[#CCFF00]">
              / TEST ABGESCHLOSSEN
            </div>
            <div className="font-heading font-bold text-xl">
              Danke, {player.name}!
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-6 py-12 space-y-12">
        {/* Trading card */}
        <PlayerCard archetype={archetype} player={player} teamName={team?.name} />

        {/* Radar + Top motives */}
        <div className="grid lg:grid-cols-2 gap-8">
          <div className="card-elevated p-6 md:p-8">
            <div className="font-display text-xs tracking-[0.3em] text-[#CCFF00] mb-3">
              / MOTIV-RADAR
            </div>
            <h3 className="font-heading font-black text-2xl mb-4">
              Deine 12 Motive
            </h3>
            <MotiveRadar scores={scores} motives={motives} />
          </div>

          <div className="card-elevated p-6 md:p-8">
            <div className="font-display text-xs tracking-[0.3em] text-[#CCFF00] mb-3">
              / DEINE STÄRKSTEN MOTIVE
            </div>
            <h3 className="font-heading font-black text-2xl mb-6">Top 3</h3>
            <div className="space-y-3">
              {top_motives.map((m, i) => {
                const Icon = getMotiveIcon(m.icon);
                const band = scoreBand(m.score);
                return (
                  <div
                    key={m.slug}
                    className="p-4 border border-white/10 rounded-md bg-[#0f1013] flex items-center gap-4"
                  >
                    <div
                      className="w-12 h-12 rounded-sm flex items-center justify-center"
                      style={{
                        background: `${m.color}22`,
                        border: `1px solid ${m.color}66`,
                        color: m.color,
                      }}
                    >
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-heading font-bold text-lg">{m.name}</div>
                      <div className="text-xs text-white/60">
                        {m.description}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-heading font-black text-2xl">
                        {m.score}
                      </div>
                      <div
                        className="font-display text-[10px] tracking-widest"
                        style={{ color: band.color }}
                      >
                        {band.label}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Position-Empfehlungen: bewusst NICHT für Spieler sichtbar –
             der Trainer sieht sie in seiner Profilansicht.
             ---------------------------------------------------------- */}

        <div className="text-center py-8">
          <p className="text-white/60 mb-6">
            Dein Trainer erhält jetzt automatisch dein vollständiges Profil und
            den PDF-Bericht.
          </p>
          <a href="/" className="btn-ghost">
            <Home className="w-5 h-5" />
            Zur Startseite
          </a>
        </div>
      </div>
    </div>
  );
}
